import {
    MutableRefObject,
    useCallback,
    useState,
    useRef,
    useEffect,
    PropsWithChildren
} from "react";

import { css } from "@compiled/react";

import UFOSegment from "@atlaskit/react-ufo/segment";

import { FeatureExposed } from "@/utils/feature-exposed";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import { OptionalExceptFor } from "@/utils/typescript-utils.ts";

import { Tabs } from "./tab-contents/tabs.tsx";
import {
    TEAM_DATA,
    TEAM_DATA_NEW_SCREENSHOTS,
    Team
} from "./tab-contents/team-data.tsx";

const DEFAULT_TEAM_DATA_ORIGINAL: ActiveTeamDataProps = {
    id: "default",
    videoLink:
        // eslint-disable-next-line local/no-non-orangelogic-images-eslint
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_Hero_FULL_LowBR.mp4",
    heroImage:
        "https://dam-cdn.atl.orangelogic.com/AssetLink/32mac5076120jymg5y7rtwu4ru5g3s6l.webp",
    heroImageDimensions: {
        imageWidth: 1920,
        imageHeight: 1080
    }
};

const DEFAULT_TEAM_DATA_NEW_SCREENSHOTS: ActiveTeamDataProps = {
    ...DEFAULT_TEAM_DATA_ORIGINAL,
    videoLink:
        "https://dam-cdn.atl.orangelogic.com/AssetLink/438r2lhmec7014pn861g30w8kx28ty08.mp4",
    heroImage:
        "https://dam-cdn.atl.orangelogic.com/AssetLink/qgeohf56651o63ux63s54547n4v512b6/software-hero-image.webp",
    heroImageDimensions: {
        imageWidth: 1984,
        imageHeight: 1042
    }
};

export const DEFAULT_TEAM_DATA = DEFAULT_TEAM_DATA_ORIGINAL;

export type ActiveTeamDataProps = OptionalExceptFor<
    Team,
    "heroImage" | "heroImageDimensions"
>;

export const HeroJiraCtaTemplate = ({ children }: PropsWithChildren) => {
    const tabsWrapperRef: MutableRefObject<HTMLDivElement | null> =
        useRef(null);
    const selectedTeamIndexRef: MutableRefObject<number | undefined> =
        useRef(undefined);

    const useNewScreenshots = checkFeatureFlagGate(
        featureFlags.gwp9214HomepageScreenshots
    );

    const teamData = useNewScreenshots ? TEAM_DATA_NEW_SCREENSHOTS : TEAM_DATA;
    const defaultTeamData = useNewScreenshots
        ? DEFAULT_TEAM_DATA_NEW_SCREENSHOTS
        : DEFAULT_TEAM_DATA_ORIGINAL;

    const [selectedTeamIndex, setSelectedTeamIndex] = useState<
        number | undefined
    >(undefined);
    const [activeTeamData, setActiveTeamData] =
        useState<ActiveTeamDataProps>(defaultTeamData);

    // Keep ref in sync with state for use in event listener
    useEffect(() => {
        selectedTeamIndexRef.current = selectedTeamIndex;
    }, [selectedTeamIndex]);

    // if a tab is currently selected and the user clicks outside of the
    // tabs container, display the default video from its beginning
    const handleClickOutside = useCallback((event: MouseEvent) => {
        if (
            typeof selectedTeamIndexRef.current === "number" &&
            tabsWrapperRef.current &&
            !tabsWrapperRef.current.contains(event.target as Node)
        ) {
            setSelectedTeamIndex(undefined);
        }
    }, []);

    useEffect(() => {
        // Attach the listener on component mount
        document.addEventListener("mousedown", handleClickOutside);

        // Detach the listener on component unmount
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [handleClickOutside]);

    useEffect(() => {
        if (typeof selectedTeamIndex !== "number") {
            setActiveTeamData(defaultTeamData);
            return;
        }
        setActiveTeamData(teamData[selectedTeamIndex] || defaultTeamData);
        return;
    }, [selectedTeamIndex, teamData, defaultTeamData]);

    return (
        <UFOSegment name="homepage-v2-hero">
            <section id="hero-jira-cta" css={jiraHeroStyles}>
                <>
                    {children}
                    <FeatureExposed
                        experimentKey="gwp-1705-homepage-hero"
                        group="Test"
                    >
                        <Tabs
                            heroSelectedTeamIndex={selectedTeamIndex}
                            onSelectHeroTab={setSelectedTeamIndex}
                            tabsWrapperRef={tabsWrapperRef}
                            {...activeTeamData}
                        />
                    </FeatureExposed>
                </>
            </section>
        </UFOSegment>
    );
};

const jiraHeroStyles = css({
    overflow: "hidden"
});

HeroJiraCtaTemplate.displayName = "HeroJiraCtaTemplate";
